/**
 * Anonymous visitor identity for first-party tracking.
 *
 * Stores a random id in localStorage so we can tell a brand-new visitor from a
 * returning one. No personal data, no cross-site tracking.
 */

const VISITOR_KEY = "myelina.visitor_id";
const FIRST_SEEN_KEY = "myelina.visitor_first_seen";

export interface VisitorIdentity {
  visitorId: string;
  isNewVisitor: boolean;
  firstSeen: string;
}

/** Read (or create) the visitor identity. Safe on SSR and blocked storage. */
export const getVisitorIdentity = (): VisitorIdentity => {
  const fallback = (): VisitorIdentity => ({
    visitorId: "unknown",
    isNewVisitor: false,
    firstSeen: new Date().toISOString(),
  });

  if (typeof window === "undefined") return fallback();

  try {
    let visitorId = localStorage.getItem(VISITOR_KEY);
    let firstSeen = localStorage.getItem(FIRST_SEEN_KEY);
    let isNewVisitor = false;

    if (!visitorId) {
      visitorId = crypto.randomUUID();
      firstSeen = new Date().toISOString();
      isNewVisitor = true;
      localStorage.setItem(VISITOR_KEY, visitorId);
      localStorage.setItem(FIRST_SEEN_KEY, firstSeen);
    }

    return { visitorId, isNewVisitor, firstSeen: firstSeen ?? new Date().toISOString() };
  } catch {
    return fallback();
  }
};
